import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular/standalone';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AppSettings, AvailableEntries, FeatureTile, MenuStage, MenuStageName, ServerSettings } from '../../models';
import { LogService } from '../log/log.service';
import { ConfigProvider } from './config-provider';

@Injectable()
export class ServerSettingsProvider implements ConfigProvider {
  private readonly http = inject(HttpClient);
  private readonly platform = inject(Platform);
  public readonly loadOrder = 3;

  public async getAppSettings(appSettings: AppSettings): Promise<AppSettings> {
    const fromConfig: ServerSettings | null = await this.loadFromFile(appSettings.connection.res);
    if (fromConfig) {
      appSettings.videos = fromConfig.videos;
      appSettings.links = fromConfig.links;
      appSettings.personalNumberLength = fromConfig.personalNumberLength || appSettings.personalNumberLength;
      appSettings.constants = fromConfig.constants;
      appSettings.releaseVersion = fromConfig.releaseVersion;
      appSettings.customizing.firstBelegMandatoryConfig = fromConfig.firstBelegMandatoryConfig ?? {
        isActive: false,
        mandatoryPages: 0,
      };
      appSettings.isMaintenance = fromConfig.isMaintenance ?? false;
      appSettings.einreichungV3Activated = fromConfig.einreichungV3Activated;

      if (!Capacitor.isNativePlatform()) {
        this.filterAvailableFeature(appSettings);
      }
    } else {
      throw new Error(`Could not load external config file from ${appSettings.connection.res}/api/file/config`);
    }
    return appSettings;
  }

  private async loadFromFile(connectUrl: string): Promise<ServerSettings | null> {
    return firstValueFrom(
      this.http.get<ServerSettings>(`${connectUrl}/api/file/config`).pipe(
        catchError((err) => {
          LogService.error(err);
          return of(null);
        }),
      ),
    );
  }

  private filterAvailableFeature(appSettings: AppSettings): void {
    const itemsToRemove: AvailableEntries[] = appSettings.einreichungV3Activated
      ? ['NEW_ANTRAG', 'ANTRAG_LIST']
      : ['MY_APPLICATIONS', 'START_EINREICHUNG'];
    if (appSettings.dashboard?.menuTiles) {
      appSettings.dashboard.menuTiles = appSettings.dashboard.menuTiles.filter(
        (tile: FeatureTile) => !itemsToRemove.includes(tile.feature),
      );
    }

    if (appSettings.customizing.menu) {
      appSettings.customizing.menu[MenuStage[2] as MenuStageName] = appSettings.customizing.menu[
        MenuStage[2] as MenuStageName
      ].filter((entry: AvailableEntries) => !itemsToRemove.includes(entry));
    }
  }
}
